import {Badge, Input} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import React, {useCallback, useEffect, useState} from 'react';
import {ViewField, ViewSwitch} from 'src/design/atoms/ViewField';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {ISelectOption} from 'src/types/Form';
import type {IFormDataV2} from 'src/types/FormV2';

interface ContentSectionProps {
  formData: IFormDataV2;
  onChange: (name: string, value: any) => void;
  mode: 'create' | 'edit' | 'view';
  personnel: ISelectOption[];
  validationFnRef?: React.MutableRefObject<(() => boolean) | undefined>;
  isIdpManaged?: boolean;
}

export const UserGroupContentTab: React.FC<ContentSectionProps> = ({
  formData,
  onChange,
  mode,
  personnel,
  validationFnRef,
  isIdpManaged = false,
}) => {
  const isViewMode = mode === 'view';
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    const name = String(formData.name || '').trim();
    if (!name) {
      newErrors.name = 'Group name is required';
    } else if (name.length < 3) {
      newErrors.name = 'Group name must be at least 3 characters';
    } else if (name.length > 100) {
      newErrors.name = 'Group name must be 100 characters or less';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name]);

  useEffect(() => {
    if (validationFnRef) {
      validationFnRef.current = validateForm;
    }
  }, [validationFnRef, validateForm]);

  const handleChange = useCallback(
    (name: string, value: any) => {
      onChange(name, value);
      if (errors[name]) {
        setErrors(prev => {
          const next = {...prev};
          delete next[name];
          return next;
        });
      }
    },
    [onChange, errors]
  );

  const memberCount = Array.isArray(formData.targetUsers)
    ? formData.targetUsers.length
    : 0;

  const resolveMembers = (): string => {
    if (!Array.isArray(formData.targetUsers) || memberCount === 0) return '—';
    return formData.targetUsers
      .map((u: any) => (typeof u === 'object' ? u.label || u.name : u))
      .join(', ');
  };

  if (isViewMode) {
    return (
      <Stack gap="md" data-testid="user-group-content-tab">
        <SectionCard title="General" data-testid="section-general">
          <Box p="md">
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewField label="Name" value={formData.name} />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewSwitch label="Active" value={!!formData.active} />
              </Grid.Col>
            </Grid>
          </Box>
        </SectionCard>

        <SectionCard title="Group Type" data-testid="section-group-type">
          <Box p="md">
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewSwitch
                  label="Notification Group"
                  value={!!formData.isNotificationGroup}
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewSwitch
                  label="Permission Group"
                  value={!!formData.isPermissionGroup}
                />
              </Grid.Col>
            </Grid>
          </Box>
        </SectionCard>

        <SectionCard
          title="Auto-Assignment"
          data-testid="section-auto-assignment">
          <Box p="md">
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewSwitch
                  label="Auto-Assign to Employees"
                  value={!!formData.isDefaultEmployeeGroup}
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 6}}>
                <ViewSwitch
                  label="Auto-Assign to Contractors"
                  value={!!formData.isDefaultContractorGroup}
                />
              </Grid.Col>
            </Grid>
          </Box>
        </SectionCard>

        <SectionCard
          title="Members"
          rightSection={
            <Badge size="sm" variant="light" color="blue">
              {memberCount} member{memberCount !== 1 ? 's' : ''}
            </Badge>
          }
          data-testid="section-members">
          <Box p="md">
            <Text size="sm">{resolveMembers()}</Text>
          </Box>
        </SectionCard>
      </Stack>
    );
  }

  return (
    <Stack gap="md" data-testid="user-group-content-tab">
      <SectionCard title="General" data-testid="section-general">
        <Box p="md">
          <Grid gutter="md">
            <Grid.Col span={{base: 12, md: 6}}>
              <Input.Wrapper
                data-testid="form-field-name"
                label="Group Name"
                description="A unique name for this group (3-100 characters)."
                error={errors.name}
                withAsterisk>
                <Input
                  data-testid="form-field-name-input"
                  value={formData.name || ''}
                  onChange={e => handleChange('name', e.currentTarget.value)}
                  placeholder="Enter group name"
                  error={!!errors.name}
                  autoComplete="new-password"
                />
              </Input.Wrapper>
            </Grid.Col>
            <Grid.Col span={{base: 12, md: 6}}>
              <Switch
                data-testid="form-field-active"
                label="Active"
                description="Inactive groups are ignored for notifications and permissions."
                color="green.8"
                checked={!!formData.active}
                onChange={() => handleChange('active', !formData.active)}
              />
            </Grid.Col>
          </Grid>
        </Box>
      </SectionCard>

      <SectionCard title="Group Type" data-testid="section-group-type">
        <Box p="md">
          <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
            <Switch
              data-testid="form-field-isNotificationGroup"
              label="Notification Group"
              description="Allow this group to be targeted by notification rules."
              color="green.8"
              checked={!!formData.isNotificationGroup}
              onChange={() =>
                handleChange(
                  'isNotificationGroup',
                  !formData.isNotificationGroup
                )
              }
            />
            <Switch
              data-testid="form-field-isPermissionGroup"
              label="Permission Group"
              description="Allow this group to be used for module and object permissions."
              color="green.8"
              checked={!!formData.isPermissionGroup}
              onChange={() =>
                handleChange('isPermissionGroup', !formData.isPermissionGroup)
              }
            />
          </SimpleGrid>
        </Box>
      </SectionCard>

      <SectionCard
        title="Auto-Assignment"
        data-testid="section-auto-assignment">
        <Box p="md">
          <SimpleGrid cols={{base: 1, md: 2}} spacing="md">
            <Switch
              data-testid="form-field-isDefaultEmployeeGroup"
              label="Auto-Assign to Employees"
              description="New employees will be automatically added to this group."
              color="green.8"
              checked={!!formData.isDefaultEmployeeGroup}
              onChange={() =>
                handleChange(
                  'isDefaultEmployeeGroup',
                  !formData.isDefaultEmployeeGroup
                )
              }
            />
            <Switch
              data-testid="form-field-isDefaultContractorGroup"
              label="Auto-Assign to Contractors"
              description="New contractors will be automatically added to this group."
              color="green.8"
              checked={!!formData.isDefaultContractorGroup}
              onChange={() =>
                handleChange(
                  'isDefaultContractorGroup',
                  !formData.isDefaultContractorGroup
                )
              }
            />
          </SimpleGrid>
        </Box>
      </SectionCard>

      <SectionCard
        title="Members"
        rightSection={
          <Badge size="sm" variant="light" color="blue">
            {memberCount} selected
          </Badge>
        }
        data-testid="section-members">
        <Box p="md">
          <Text size="sm" c="dimmed" mb="sm">
            {isIdpManaged
              ? 'Membership is managed by your identity provider and cannot be edited here.'
              : 'Select personnel to include in this group. Members will receive notifications and/or permissions based on the group configuration.'}
          </Text>
          <FormItemMemberSelect
            formData={formData}
            onChange={handleChange}
            personnel={personnel}
            disabled={isIdpManaged}
          />
        </Box>
      </SectionCard>
    </Stack>
  );
};

// Lightweight wrappers that use the existing FormSelectModalV2 for multi-select
import {FormSelectModalV2} from 'src/app/forms/FormSelectModal/FormSelectModalV2';

const memberField = {
  name: 'targetUsers',
  label: 'Group Members',
  type: 'select-multiple' as const,
  options: [] as ISelectOption[],
};

const FormItemMemberSelect: React.FC<{
  formData: IFormDataV2;
  onChange: (name: string, value: any) => void;
  personnel: ISelectOption[];
  disabled?: boolean;
}> = ({formData, onChange, personnel, disabled}) => (
  <FormSelectModalV2
    field={{...memberField, options: personnel, disabled: disabled}}
    value={formData.targetUsers || []}
    onChange={onChange}
  />
);
